import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { AuditEventTable } from "../audit/AuditTrail";
import { S } from "../../styles";
import { LoadingState } from "../../components/QueryStates";

/**
 * What happened to this shipment, in the order it happened.
 *
 * The `AuditTrail` component next door takes one resource and lists its events,
 * which is right for a PO or an RFQ. A receiving inspection does not live under
 * one id: the verdict is recorded against the shipment, the certificates that
 * produced it are `trace_document` events, and the goods-in is on the purchase
 * order. So this calls a route that assembles the three and interleaves them,
 * and reuses the same table to render the result.
 *
 * The interleaving is the point rather than a convenience. A certificate set
 * aside *before* an acceptance and one set aside after tell different stories
 * about the same two events, and three separate lists leave the reader to
 * reconstruct which happened — which is exactly the reconstruction an audit
 * trail exists to spare them.
 *
 * Lazily fetched: most cards are never opened this far, and the query costs a
 * document lookup plus two audit reads.
 */
export function ShipmentHistory({ shipmentId }: { shipmentId: string }) {
  const [open, setOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["shipment-history", shipmentId],
    queryFn: () => api.getShipmentHistory(shipmentId),
    enabled: open,
  });

  const events = data ?? [];

  return (
    <div style={{ marginTop: 12 }}>
      <button
        style={{ ...S.btnSm, background: "#495057" }}
        onClick={() => setOpen((x) => !x)}
      >
        🕓 {open ? "Hide" : "Show"} Inspection History
        {data ? ` (${data.length})` : ""}
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {isLoading && <LoadingState label="Loading history…" />}
          {error && <p style={S.err}>⚠ {error.message}</p>}
          {!isLoading && !error && events.length === 0 && (
            <p style={{ ...S.muted, fontSize: 12 }}>
              Nothing recorded against this shipment yet.
            </p>
          )}
          {events.length > 0 && (
            <>
              {/*
                Resource shown: the rows come from three record types, and
                "which certificate" is the difference between two otherwise
                identical exclusion events.
              */}
              <AuditEventTable events={events} showResource />
              <div style={{ marginTop: 6 }}>
                <button style={S.btnSm} onClick={() => void refetch()}>
                  ↺ Refresh
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
